import { createCodedError } from '~/common/errors/CodedError';
import type { EsignatureIntegrationType } from '~/features/admin/ESignature/types';
import { JsonApiErrorResponse } from '~/redux/api/ApiTypes';
import { HttpValidationError } from '~/redux/api/types/sylvanus/internal';
import { testIsAxiosError } from '~/redux/api/utils';
import { esignature } from '~/services/serviceInstances';

export interface Request {
  integrationId: string;
  integrationType: EsignatureIntegrationType;
}

type ServerRequest = {
  data: {
    type: string;
    attributes: {
      type: string;
    };
  };
};

export type UpdateEsignatureIntegrationResponse = void;

type ErrorResponse = JsonApiErrorResponse | HttpValidationError;

export const updateEsignatureIntegration = async (
  request: Request,
): Promise<UpdateEsignatureIntegrationResponse> => {
  const serverRequest = mapRequest(request);

  try {
    await esignature.patch(
      `/integrations/${request.integrationId}`,
      serverRequest,
    );
  } catch (error) {
    if (testIsAxiosError<ErrorResponse>(error) && error.response) {
      const { status } = error.response;
      const responseData = error.response.data;

      if (
        status === 422 &&
        'detail' in responseData &&
        Array.isArray(responseData.detail)
      ) {
        const validationError = responseData.detail[0];
        const message = validationError?.msg || 'Validation error';
        throw createCodedError({
          message,
          statusCode: status,
          title: 'Validation Error',
        });
      }

      if ('errors' in responseData && Array.isArray(responseData.errors)) {
        const errorResponse = responseData.errors[0];
        throw createCodedError({
          message: errorResponse.detail ?? errorResponse.title,
          statusCode: errorResponse.status,
          title: errorResponse.title,
        });
      }
    }
    throw error;
  }
};

const mapRequest = (request: Request): ServerRequest => {
  return {
    data: {
      type: 'integrations',
      attributes: {
        type: request.integrationType.toUpperCase(),
      },
    },
  };
};
